/**
 * The date window shared by every ranged admin view — the dashboard, the transaction queues and
 * the payments ledger.
 *
 * ## Why one component
 *
 * These three pages are read against each other: an admin checks the dashboard, clicks through to
 * the queue the number came from, then checks the money it produced. Before this they each had a
 * different control (the dashboard: two date inputs plus 7d/30d/90d buttons; the queues: a
 * "Last N days" dropdown offering 7/14/21/28; payments: nothing at all), a different option set
 * and a different default — so the same three figures on three pages described three different
 * spans of time, and nothing on screen said so.
 *
 * ## Calendar *and* presets
 *
 * The calendar is always visible rather than hidden behind a "Custom…" mode: a range you cannot
 * see is one you have to open a menu to check, and the whole point of this control is that the
 * window on screen is never in doubt. The preset dropdown sits beside it because "last 30 days" is
 * the overwhelmingly common request and typing two dates for it is three interactions too many.
 * Picking a preset fills the calendar; editing either date drops the dropdown to "Custom".
 *
 * ## The default comes from Settings
 *
 * `default_range_days` in site config (Admin → Settings), the same mechanism as `page_size`. It is
 * resolved through `loadSiteConfig()`, which resolves even when the request fails, so a page can
 * always reach a usable window — a dashboard that renders nothing because a config lookup timed
 * out is a worse failure than one showing the wrong month.
 */
import { useEffect, useMemo, useState } from "react";

import { loadSiteConfig } from "@/lib/siteConfig";

/** The spans the picker offers and Settings allows. Mirrors RANGE_DAYS_CHOICES in routers/public.py. */
export const RANGE_DAYS_CHOICES = [7, 14, 30, 90];
export const DEFAULT_RANGE_DAYS = 30;

/**
 * "All time", as a real date rather than an absent one.
 *
 * An empty `from` would mean two different things to the two consumers — the server reads a missing
 * `from` as "apply the default window", the client reads it as "no filter" — and that disagreement
 * is exactly how a page ends up showing one range and reporting another. A date older than any
 * record says the same thing to both.
 */
export const ALL_TIME = "1970-01-01";

const pad2 = (n) => String(n).padStart(2, "0");

/** A Date's own local calendar date as `yyyy-mm-dd`.
 *
 * Not `.toISOString().slice(0, 10)` — that converts to UTC first, so anywhere east of Greenwich it
 * reports *yesterday's* date for the first few hours after local midnight (and west of Greenwich,
 * tomorrow's, for the last few hours before it). A transaction created five minutes ago at 1am
 * local would then fall after the resulting "today" cutoff and read as hidden by the date filter —
 * which is exactly the bug this replaced: a fresh transaction reported as an "older transaction
 * hidden by the last 30 days filter".
 */
const isoFromLocalDate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

export const isoToday = () => isoFromLocalDate(new Date());

/** ISO date (yyyy-mm-dd) `days` before today, in local calendar days. */
export const isoDaysAgo = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return isoFromLocalDate(d);
};

const isIso = (v) => typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(v));

/** The window as a preset span in days, or `null` when the dates are a hand-picked range. */
export function rangeToDays({ from, to }) {
  if (from === ALL_TIME) return 0;
  if (to !== isoToday()) return null;
  return RANGE_DAYS_CHOICES.find(n => isoDaysAgo(n) === from) ?? null;
}

/** A preset span in days as a window. `0` is All time. */
export const daysToRange = (days) =>
  (days === 0 ? { from: ALL_TIME, to: isoToday() } : { from: isoDaysAgo(days), to: isoToday() });

/**
 * Is `iso` inside the window?
 *
 * `to` is **inclusive** — picking the 30th means including the 30th — so the comparison is against
 * the following midnight, mirroring `_parse_range()` in backend/routers/admin.py. Comparing
 * `<= to` instead is the classic off-by-one that silently drops everything that happened after
 * midnight on the last day of the range, which on the payments page is a day's revenue.
 *
 * An undated row is never hidden: a filter is not the place to make a record disappear.
 */
export function withinRange(iso, from, to) {
  if (!from || !to) return true;
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return true;
  const start = Date.parse(`${from}T00:00:00`);
  const end = Date.parse(`${to}T00:00:00`) + 86400000;
  return ts >= start && ts < end;
}

/**
 * The current window, seeded from the URL and otherwise from Settings.
 *
 * Returns `{ from, to }` as `null` until the default has resolved, so a page never fires its first
 * fetch against a guessed window and then again against the real one. Callers render their normal
 * loading state while `ready` is false — which for two of the three pages is the state they were
 * already in, waiting on their own data.
 */
export function useDateRange(params) {
  const urlFrom = params?.get("from");
  const urlTo = params?.get("to");
  const seeded = isIso(urlFrom) && isIso(urlTo) ? { from: urlFrom, to: urlTo } : null;
  const [range, setRange] = useState(seeded);

  useEffect(() => {
    if (range) return;
    let alive = true;
    loadSiteConfig().then(cfg => {
      if (!alive) return;
      const d = Number(cfg?.default_range_days);
      setRange(daysToRange(RANGE_DAYS_CHOICES.includes(d) ? d : DEFAULT_RANGE_DAYS));
    });
    return () => { alive = false; };
    // Runs once: after the default lands, `range` is set and the guard above holds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    from: range?.from ?? null,
    to: range?.to ?? null,
    ready: Boolean(range),
    setRange,
    setDays: (days) => setRange(daysToRange(days)),
  };
}

/**
 * The window in prose, for the sentences that have to name it — "3 older transactions hidden by
 * the last 30 days filter". Reads as a preset when it is one, and as two dates when it is not.
 */
export function rangeLabel(from, to) {
  if (from === ALL_TIME) return "all time";
  const days = rangeToDays({ from, to });
  if (days) return `last ${days} days`;
  const fmt = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString(undefined,
                                                                     { day: "numeric", month: "short" });
  return `${fmt(from)} – ${fmt(to)}`;
}

const INPUT = "bg-[#080C16] border border-white/15 rounded-lg px-2.5 py-1.5 text-xs text-white "
  + "focus:outline-none focus:border-[#CBA96C]/50 [color-scheme:dark]";

/**
 * The control itself. `onChange({ from, to })` is called with the whole window, never one half —
 * a consumer that had to reassemble it could observe a from/to pair that was briefly inverted.
 */
export function DateRange({ from, to, onChange, testid = "range" }) {
  const days = useMemo(() => rangeToDays({ from, to }), [from, to]);
  if (!from || !to) return null;

  const label = days === null ? "custom" : String(days);
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={testid}>
      <select
        data-testid={`${testid}-preset`} value={label} aria-label="Date range preset"
        onChange={e => { if (e.target.value !== "custom") onChange(daysToRange(Number(e.target.value))); }}
        className={INPUT}
      >
        {RANGE_DAYS_CHOICES.map(n => <option key={n} value={n}>Last {n} days</option>)}
        <option value="0">All time</option>
        {/* Present only while it applies. A permanent "Custom…" entry reads as an action, and
            picking it would have to do something — there is no third window to switch to. */}
        {days === null && <option value="custom">Custom</option>}
      </select>
      <input
        type="date" value={from === ALL_TIME ? "" : from} max={to}
        onChange={e => e.target.value && onChange({ from: e.target.value, to })}
        data-testid={`${testid}-from`} aria-label="From date" className={INPUT}
        placeholder="All time"
      />
      <span className="text-xs text-[#6B7280]">to</span>
      <input
        type="date" value={to} min={from === ALL_TIME ? undefined : from} max={isoToday()}
        onChange={e => e.target.value && onChange({ from, to: e.target.value })}
        data-testid={`${testid}-to`} aria-label="To date" className={INPUT}
      />
    </div>
  );
}

export default DateRange;
